import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Edit, Trash2, Rows } from "lucide-react";
import { getApiErrorPayload, tablesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Loading } from "@/components/ui/loader";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface RestaurantTable {
	id: number;
	number: string;
	label: string | null;
	floor: string | null;
	zone: string | null;
	seats: number | null;
	sortOrder: number;
	isActive: boolean;
	branchId: number;
}

interface TableForm {
	number: string;
	label: string;
	floor: string;
	zone: string;
	seats: string;
	sortOrder: number;
	isActive: boolean;
}

interface BulkForm {
	from: number;
	to: number;
	prefix: string;
	floor: string;
	zone: string;
	seats: string;
}

const emptyForm: TableForm = { number: "", label: "", floor: "", zone: "", seats: "", sortOrder: 0, isActive: true };
const emptyBulk: BulkForm = { from: 1, to: 10, prefix: "", floor: "", zone: "", seats: "" };

/** Branch-scoped table CRUD (create / edit / generate range / delete). Embedded in the POS detail page. */
export function TablesManager({ branchId }: { branchId: number }) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [tables, setTables] = useState<RestaurantTable[]>([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [bulkOpen, setBulkOpen] = useState(false);
	const [editing, setEditing] = useState<RestaurantTable | null>(null);
	const [form, setForm] = useState<TableForm>(emptyForm);
	const [bulk, setBulk] = useState<BulkForm>(emptyBulk);
	const [saving, setSaving] = useState(false);

	const showError = useCallback(
		(error: unknown) => {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		},
		[t, toast],
	);

	const fetchTables = useCallback(() => {
		setLoading(true);
		tablesAPI
			.getAll(branchId)
			.then((data: RestaurantTable[]) => setTables(data))
			.catch(showError)
			.finally(() => setLoading(false));
	}, [branchId, showError]);

	useEffect(() => {
		fetchTables();
	}, [fetchTables]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setDialogOpen(true);
	};

	const openEdit = (tbl: RestaurantTable) => {
		setEditing(tbl);
		setForm({
			number: tbl.number,
			label: tbl.label ?? "",
			floor: tbl.floor ?? "",
			zone: tbl.zone ?? "",
			seats: tbl.seats != null ? String(tbl.seats) : "",
			sortOrder: tbl.sortOrder,
			isActive: tbl.isActive,
		});
		setDialogOpen(true);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		const payload = {
			branchId,
			number: form.number.trim(),
			label: form.label.trim() || undefined,
			floor: form.floor.trim() || undefined,
			zone: form.zone.trim() || undefined,
			seats: form.seats.trim() ? parseInt(form.seats, 10) : undefined,
			sortOrder: form.sortOrder,
			isActive: form.isActive,
		};
		try {
			if (editing) {
				await tablesAPI.update(editing.id, payload);
				toast({ title: t("common.success"), description: t("tablesAdmin.messages.updated") });
			} else {
				await tablesAPI.create(payload);
				toast({ title: t("common.success"), description: t("tablesAdmin.messages.created") });
			}
			setDialogOpen(false);
			fetchTables();
		} catch (error) {
			showError(error);
		} finally {
			setSaving(false);
		}
	};

	const handleBulk = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		try {
			const created = await tablesAPI.bulkCreate({
				branchId,
				from: bulk.from,
				to: bulk.to,
				prefix: bulk.prefix.trim() || undefined,
				floor: bulk.floor.trim() || undefined,
				zone: bulk.zone.trim() || undefined,
				seats: bulk.seats.trim() ? parseInt(bulk.seats, 10) : undefined,
			});
			toast({
				title: t("common.success"),
				description: t("tablesAdmin.messages.generated", { count: Array.isArray(created) ? created.length : 0 }),
			});
			setBulkOpen(false);
			setBulk(emptyBulk);
			fetchTables();
		} catch (error) {
			showError(error);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (tbl: RestaurantTable) => {
		if (!(await confirmAction(t("tablesAdmin.confirmDelete", { number: tbl.number }), { confirmLabel: t("common.delete") }))) return;
		try {
			await tablesAPI.delete(tbl.id);
			toast({ title: t("common.success"), description: t("tablesAdmin.messages.deleted") });
			fetchTables();
		} catch (error) {
			showError(error);
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-end gap-2">
				<PermissionGate permission="settings.create">
					<Button type="button" variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
						<Rows className="me-2 h-4 w-4" />
						{t("tablesAdmin.generate")}
					</Button>
					<Button type="button" size="sm" onClick={openCreate}>
						<Plus className="me-2 h-4 w-4" />
						{t("tablesAdmin.add")}
					</Button>
				</PermissionGate>
			</div>

			{loading ? (
				<Loading />
			) : tables.length === 0 ? (
				<p className="text-muted-foreground py-8 text-center text-sm">{t("tablesAdmin.empty")}</p>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t("tablesAdmin.table.number")}</TableHead>
							<TableHead>{t("tablesAdmin.table.label")}</TableHead>
							<TableHead>{t("tablesAdmin.table.floor")}</TableHead>
							<TableHead>{t("tablesAdmin.table.zone")}</TableHead>
							<TableHead className="text-center">{t("tablesAdmin.table.seats")}</TableHead>
							<TableHead className="text-center">{t("tablesAdmin.table.status")}</TableHead>
							<TableHead className="text-end">{t("tablesAdmin.table.actions")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{tables.map(tbl => (
							<TableRow key={tbl.id}>
								<TableCell className="font-semibold">{tbl.number}</TableCell>
								<TableCell>{tbl.label || "-"}</TableCell>
								<TableCell>{tbl.floor || "-"}</TableCell>
								<TableCell>{tbl.zone || "-"}</TableCell>
								<TableCell className="text-center">{tbl.seats ?? "-"}</TableCell>
								<TableCell className="text-center">
									<Badge variant={tbl.isActive ? "success" : "secondary"}>
										{tbl.isActive ? t("tablesAdmin.active") : t("tablesAdmin.inactive")}
									</Badge>
								</TableCell>
								<TableCell className="text-end">
									<div className="flex justify-end gap-1">
										<PermissionGate permission="settings.update">
											<Button
												title={t("common.edit")}
												aria-label={t("common.edit")}
												variant="ghost"
												size="icon"
												onClick={() => openEdit(tbl)}
											>
												<Edit className="h-4 w-4" />
											</Button>
										</PermissionGate>
										<PermissionGate permission="settings.delete">
											<Button
												title={t("common.delete")}
												aria-label={t("common.delete")}
												variant="ghost"
												size="icon"
												className="text-destructive"
												onClick={() => handleDelete(tbl)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</PermissionGate>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? t("tablesAdmin.editTitle") : t("tablesAdmin.add")}</DialogTitle>
						<DialogDescription>{t("tablesAdmin.subtitle")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="tm-number">{t("tablesAdmin.form.number")}</Label>
								<Input
									id="tm-number"
									value={form.number}
									onChange={e => setForm({ ...form, number: e.target.value })}
									placeholder={t("tablesAdmin.form.numberPlaceholder")}
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-label">{t("tablesAdmin.form.label")}</Label>
								<Input
									id="tm-label"
									value={form.label}
									onChange={e => setForm({ ...form, label: e.target.value })}
									placeholder={t("tablesAdmin.form.labelPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-floor">{t("tablesAdmin.form.floor")}</Label>
								<Input
									id="tm-floor"
									value={form.floor}
									onChange={e => setForm({ ...form, floor: e.target.value })}
									placeholder={t("tablesAdmin.form.floorPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-zone">{t("tablesAdmin.form.zone")}</Label>
								<Input
									id="tm-zone"
									value={form.zone}
									onChange={e => setForm({ ...form, zone: e.target.value })}
									placeholder={t("tablesAdmin.form.zonePlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-seats">{t("tablesAdmin.form.seats")}</Label>
								<Input
									id="tm-seats"
									type="number"
									min="1"
									value={form.seats}
									onChange={e => setForm({ ...form, seats: e.target.value })}
									placeholder={t("tablesAdmin.form.seatsPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-sortOrder">{t("tablesAdmin.form.sortOrder")}</Label>
								<Input
									id="tm-sortOrder"
									type="number"
									min="0"
									value={form.sortOrder}
									onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
								/>
							</div>
							<div className="flex items-center justify-between pt-6">
								<Label htmlFor="tm-isActive">{t("tablesAdmin.form.isActive")}</Label>
								<Switch
									id="tm-isActive"
									checked={form.isActive}
									onCheckedChange={checked => setForm({ ...form, isActive: checked })}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={saving}>
								{editing ? t("common.update") : t("common.create")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			<Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("tablesAdmin.bulk.title")}</DialogTitle>
						<DialogDescription>{t("tablesAdmin.bulk.description")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleBulk} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="tm-from">{t("tablesAdmin.bulk.from")}</Label>
								<Input
									id="tm-from"
									type="number"
									min="0"
									value={bulk.from}
									onChange={e => setBulk({ ...bulk, from: parseInt(e.target.value) || 0 })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-to">{t("tablesAdmin.bulk.to")}</Label>
								<Input
									id="tm-to"
									type="number"
									min="0"
									value={bulk.to}
									onChange={e => setBulk({ ...bulk, to: parseInt(e.target.value) || 0 })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-prefix">{t("tablesAdmin.bulk.prefix")}</Label>
								<Input
									id="tm-prefix"
									value={bulk.prefix}
									onChange={e => setBulk({ ...bulk, prefix: e.target.value })}
									placeholder={t("tablesAdmin.bulk.prefixPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-bulkSeats">{t("tablesAdmin.form.seats")}</Label>
								<Input
									id="tm-bulkSeats"
									type="number"
									min="1"
									value={bulk.seats}
									onChange={e => setBulk({ ...bulk, seats: e.target.value })}
									placeholder={t("tablesAdmin.form.seatsPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-bulkFloor">{t("tablesAdmin.form.floor")}</Label>
								<Input
									id="tm-bulkFloor"
									value={bulk.floor}
									onChange={e => setBulk({ ...bulk, floor: e.target.value })}
									placeholder={t("tablesAdmin.form.floorPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="tm-bulkZone">{t("tablesAdmin.form.zone")}</Label>
								<Input
									id="tm-bulkZone"
									value={bulk.zone}
									onChange={e => setBulk({ ...bulk, zone: e.target.value })}
									placeholder={t("tablesAdmin.form.zonePlaceholder")}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={saving}>
								{t("tablesAdmin.generate")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
